import type { VectorDataSource, VectorFormat } from '../core/types';

/**
 * Result of format detection for a data source.
 */
export interface DetectedSource {
  /** Detected vector format */
  format: VectorFormat;
  /** Suggested display name (file name without extension, or 'GeoJSON') */
  name: string;
}

/**
 * Maps file extensions to vector formats.
 */
const EXTENSION_FORMATS: Record<string, VectorFormat> = {
  geojson: 'geojson',
  json: 'geojson',
  gpkg: 'geopackage',
  shp: 'shapefile',
  zip: 'shapefile',
  parquet: 'geoparquet',
  geoparquet: 'geoparquet',
  pq: 'geoparquet',
  fgb: 'flatgeobuf',
  csv: 'csv',
  tsv: 'csv',
  // Not a GDAL-readable path on its own: the engine unzips a KMZ and reads
  // the KML inside it (see formats/kmz.ts).
  kmz: 'kmz',
};

/**
 * Extracts the file name from a URL or path, stripping query strings.
 *
 * @param url - URL or file path
 * @returns The trailing file name segment
 */
export function fileNameFromUrl(url: string): string {
  const withoutQuery = url.split(/[?#]/)[0];
  const segments = withoutQuery.split('/');
  return segments[segments.length - 1] || withoutQuery;
}

/**
 * Detects the vector format from a file name based on its extension.
 *
 * Extensions without a dedicated reader (kml, gml, tab, dxf, ...) are
 * returned as-is and handled by the GDAL-backed ST_Read, so every
 * format the spatial extension supports can load.
 *
 * @param fileName - File name or URL path
 * @returns The detected format, or 'unknown' when there is no extension
 */
export function formatFromFileName(fileName: string): VectorFormat {
  const match = /\.([a-z0-9]+)$/i.exec(fileName.trim());
  if (!match) return 'unknown';
  const extension = match[1].toLowerCase();
  return EXTENSION_FORMATS[extension] ?? extension;
}

/**
 * Strips the extension from a file name for display purposes.
 *
 * @param fileName - File name
 * @returns File name without its extension
 */
export function baseName(fileName: string): string {
  return fileName.replace(/\.[a-z0-9]+$/i, '') || fileName;
}

/**
 * Maps data: URL MIME types to vector formats.
 */
const MIME_FORMATS: Array<[RegExp, VectorFormat]> = [
  [/json/, 'geojson'],
  [/csv|tab-separated/, 'csv'],
  [/parquet/, 'geoparquet'],
];

/**
 * Detects the format of a data: URL from its MIME type.
 *
 * Bundlers (e.g. Vite) inline small assets as base64 data URLs, so a
 * sample.geojson import can reach addData as `data:application/geo+json;...`.
 *
 * @param url - The data: URL
 * @returns The detected format, or 'unknown'
 */
export function formatFromDataUrl(url: string): VectorFormat {
  const mime = url.slice('data:'.length).split(/[;,]/)[0].toLowerCase();
  for (const [pattern, format] of MIME_FORMATS) {
    if (pattern.test(mime)) return format;
  }
  return 'unknown';
}

/**
 * Detects the format and display name of a data source.
 *
 * GeoJSON objects are recognized directly; files and URLs are detected
 * from their extension (or MIME type for data: URLs). An explicit
 * format always wins.
 *
 * @param source - URL string, File/Blob, or GeoJSON object
 * @param explicitFormat - Optional format override
 * @returns The detected source description
 */
export function detectSource(
  source: VectorDataSource,
  explicitFormat?: VectorFormat,
): DetectedSource {
  if (typeof source === 'string') {
    if (source.startsWith('data:')) {
      return { format: explicitFormat ?? formatFromDataUrl(source), name: 'Untitled' };
    }
    const name = fileNameFromUrl(source);
    return {
      format: explicitFormat ?? formatFromFileName(name),
      name: baseName(name),
    };
  }

  if (typeof File !== 'undefined' && source instanceof File) {
    return {
      format: explicitFormat ?? formatFromFileName(source.name),
      name: baseName(source.name),
    };
  }

  if (typeof Blob !== 'undefined' && source instanceof Blob) {
    return { format: explicitFormat ?? 'unknown', name: 'Untitled' };
  }

  // Plain object - treat as GeoJSON
  return { format: explicitFormat ?? 'geojson', name: 'GeoJSON' };
}
